// Small, versioned data contracts for optional practice support. No curriculum scheduler.
import { z } from 'zod';

/** check grades the page; talk is a typed message (a question or a worded answer) that never creates assessment evidence. */
export const TutorIntent = z.enum(['check', 'hint', 'explain', 'talk']);
export const PracticeDemand = z.enum(['recall', 'discriminate', 'connect']);
export const PracticeScope = z.enum(['lesson', 'course']);
export const Sitting = z.object({
  id: z.string(), lessonId: z.string(), scope: PracticeScope,
  status: z.enum(['active', 'closing', 'ended']), pendingRetests: z.number().int().nonnegative(),
});
export const OpenSittingReq = z.object({ lessonId: z.string(), scope: PracticeScope.optional(), sittingId: z.string().nullable().optional() });
export const FinishSittingReq = z.object({ skipRetests: z.boolean().optional() });

// The deck adapter deliberately accepts a small data-only subset of the lab engine.
const Point = z.tuple([z.number().min(6).max(154), z.number().min(30).max(81)]);
const Color = z.enum(['navy', 'cobalt', 'persimmon', 'haze', 'slate', 'sage', 'cyan']);
const Base = z.object({ id: z.string().regex(/^[a-z][a-z0-9_-]{0,39}$/), at: Point, c: Color });
export const VisualObject = z.discriminatedUnion('kind', [
  Base.extend({ kind: z.literal('text'), text: z.string().min(1).max(100) }),
  Base.extend({ kind: z.literal('vector'), values: z.array(z.number().finite().min(-1e6).max(1e6)).min(1).max(6) }),
  Base.extend({ kind: z.literal('box'), text: z.string().min(1).max(60) }),
  Base.extend({ kind: z.literal('bars'), values: z.array(z.number().finite().min(0).max(1e6)).min(1).max(6), labels: z.array(z.string().max(16)).min(1).max(6) }),
]);
/** An animated explanation is a background job: a draft plays after ~1.5 min, the polished video replaces it at ~3.5 min. */
export const VideoStage = z.enum(['queued', 'planning', 'writing', 'voicing', 'drafting', 'draft', 'polishing', 'done', 'failed']);
export const VideoJob = z.object({
  id: z.string(), stage: VideoStage, title: z.string().max(140).nullable(),
  draftUrl: z.string().nullable(), videoUrl: z.string().nullable(), error: z.string().max(300).nullable(),
  elapsedSeconds: z.number().nonnegative(),
});
export const Explanation = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('diagram'), title: z.string().min(1).max(100), source: z.string().min(1).max(4000) }),
  z.object({ kind: z.literal('deck'), title: z.string().min(1).max(100), steps: z.array(z.object({
    caption: z.string().min(1).max(160), objects: z.array(VisualObject).min(1).max(8),
  })).min(2).max(8) }),
  z.object({ kind: z.literal('video'), title: z.string().min(1).max(140), job: VideoJob }),
]);
export const ExplainReq = z.object({ lessonId: z.string(), checkpointId: z.string(), format: z.enum(['diagram', 'deck', 'video']), ask: z.string().max(400).nullable().optional(), attemptId: z.string().nullable().optional() });
export const ConceptMap = z.object({
  courseId: z.string(),
  nodes: z.array(z.object({ id: z.string(), label: z.string(), lessonId: z.string(), lessonTitle: z.string() })),
  links: z.array(z.object({ from: z.string(), to: z.string(), label: z.string(), lessonId: z.string(), provenance: z.enum(['lesson-authored', 'source-mention']), quote: z.string().max(4000).optional(), sectionId: z.string().optional() })),
});

export type TutorIntent = z.infer<typeof TutorIntent>;
export type PracticeDemand = z.infer<typeof PracticeDemand>;
export type Sitting = z.infer<typeof Sitting>;
export type Explanation = z.infer<typeof Explanation>;
export type VideoJob = z.infer<typeof VideoJob>;
export type VideoStage = z.infer<typeof VideoStage>;
export type ConceptMap = z.infer<typeof ConceptMap>;
